import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpDown, Calendar, CheckCircle2, ChevronDown, ChevronRight, ChevronUp, ExternalLink,
  FileText, MessageSquare, Pencil, Printer, Trash2,
} from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { getProductTypeName, UNCLASSIFIED } from '@/lib/constants';
import { needsPlate, plateStatus } from '@/lib/plate';
import { SplitBadge, BatchSplitSummary } from '@/components/BatchSplitIndicator';
import { ActionRow, Card, StatusPill } from '@/components/phone/primitives';
import { Colors, statusTone } from '@/components/phone/theme';
import ProductionPipeline from './ProductionPipeline';
import BatchHistoryPanel from './BatchHistoryPanel';

/**
 * The order's items, on a phone.
 *
 * The desktop card is the same information, but its buttons are 24px and its
 * batch moves sit in a row that wraps under the batch they belong to. Here each
 * item is a card that says what it is, where it stands and what may be done to
 * it as one row of thumb-sized actions, and each batch is a line with its two
 * moves beneath it. The page keeps every handler; this only draws them.
 */

const ITEM_STATUS_LABEL = {
  in_production: 'In Production',
  design: 'In Design',
};

const statusLabel = (status) => ITEM_STATUS_LABEL[status]
  || (status ? status.charAt(0).toUpperCase() + status.slice(1) : '');

const PLATE_PILL = {
  received: ['Plate received', 'positive'],
  ordered: ['Plate ordered', 'accent'],
  pending: ['Plate required', 'critical'],
};

function Caption({ children, className = '' }) {
  return (
    <p className={`text-[12px] ${className}`} style={{ color: Colors.text.secondary }}>{children}</p>
  );
}

function PlateBlock({ item, onManage }) {
  const state = plateStatus(item);
  const activeBatch = item.batches?.find(b => b.status === 'in_progress');
  const stageKey = activeBatch && item.production_stages?.[activeBatch.current_stage_index]?.stage_key;
  const blocked = (stageKey?.endsWith('_printing') || stageKey === 'plate_making') && state !== 'received';

  return (
    <div
      className="mt-3 rounded-[10px] border px-3 py-2.5"
      style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4' }}
      data-testid="plate-block"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: Colors.semantic.caution }}>
          <Printer className="h-3.5 w-3.5" /> Plate
        </p>
        <StatusPill label={PLATE_PILL[state][0]} tone={PLATE_PILL[state][1]} />
      </div>
      {(item.plate_ordered_at || item.plate_received_at) && (
        <Caption className="mt-1.5">
          {item.plate_ordered_at && <span className="block">Ordered {formatIST(item.plate_ordered_at)}</span>}
          {item.plate_received_at && <span className="block">Received {formatIST(item.plate_received_at)}</span>}
        </Caption>
      )}
      {state === 'received' ? (
        <p className="mt-1.5 flex items-center gap-1 text-[12px]" style={{ color: Colors.semantic.positive }}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Plate ready for printing
        </p>
      ) : (
        <button
          type="button"
          onClick={onManage}
          className="mt-2 flex h-10 items-center gap-1 text-[13px] font-semibold"
          style={{ color: Colors.accent.DEFAULT }}
        >
          Manage in Plate Tracking <ChevronRight className="h-4 w-4" />
        </button>
      )}
      {blocked && (
        <p
          className="mt-2 rounded-md border px-2.5 py-2 text-[12px] font-medium"
          style={{ backgroundColor: Colors.semantic.criticalWash, borderColor: '#EBD4D2', color: Colors.semantic.critical }}
        >
          Plate not received — printing is blocked until it is marked as received.
        </p>
      )}
    </div>
  );
}

function BatchRow({ item, batch, canMoveStages, moveLoading, onMoveBack, onMoveForward, expanded, onToggle }) {
  const index = batch.current_stage_index || 0;
  const stages = item.production_stages || [];
  const prevStage = index > 0 ? stages[index - 1]?.stage_name : null;
  const nextStage = index < stages.length - 1 ? stages[index + 1]?.stage_name : null;
  const hasHistory = batch.stage_history?.length > 0;
  const hasRemarks = batch.stage_history?.some(h => h.remarks && h.remarks.trim());
  const hasSplitInfo = batch.split_from || batch.has_splits || batch.child_batch_ids?.length > 0;
  const done = batch.status === 'completed';

  return (
    <div
      className="rounded-[10px] border"
      style={{ borderColor: Colors.border.default }}
      data-testid={`batch-${batch.id}`}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[14px] font-bold" style={{ color: Colors.text.primary }}>#{batch.batch_number}</span>
            <Caption>{batch.quantity?.toLocaleString()} pcs</Caption>
            <StatusPill label={done ? 'Done' : (batch.current_stage_name || '—')} tone={done ? 'positive' : 'accent'} />
            <SplitBadge batch={batch} />
            {batch.revisited_stages?.length > 0 && <StatusPill label="Revisited" tone="caution" />}
          </div>
          {hasHistory && (
            <Caption className="mt-1 flex items-center gap-1">
              {batch.stage_history.length} stage{batch.stage_history.length === 1 ? '' : 's'} done
              {hasRemarks && <MessageSquare className="h-3 w-3" style={{ color: Colors.semantic.caution }} />}
            </Caption>
          )}
        </div>
        {(hasHistory || hasSplitInfo) && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? 'Hide batch history' : 'Show batch history'}
            data-testid={`batch-history-${batch.id}`}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: Colors.background.secondary, color: Colors.text.secondary }}
          >
            <ChevronDown className={`h-5 w-5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {canMoveStages && !done && (prevStage || nextStage) && (
        <div className="px-3">
          <ActionRow
            actions={[
              prevStage && {
                key: 'back', label: `Back to ${prevStage}`, icon: ChevronUp, tone: 'caution', disabled: moveLoading,
                testID: `move-back-${batch.id}`,
                onClick: () => onMoveBack({
                  jobCardId: item.job_card_id,
                  batchId: batch.batch_id || batch.id,
                  fromStage: batch.current_stage_name,
                  toStage: prevStage,
                }),
              },
              nextStage && {
                key: 'forward', label: `Move to ${nextStage}`, icon: ChevronDown, tone: 'positive', disabled: moveLoading,
                testID: `move-forward-${batch.id}`,
                onClick: () => onMoveForward(item.job_card_id, batch.batch_id || batch.id),
              },
            ]}
          />
        </div>
      )}

      {expanded && <BatchHistoryPanel batch={batch} />}
    </div>
  );
}

function ItemCard({
  item, idx, order, canSeeFinancials, canSendExternal, openSendExternal, canMoveStages, handleMoveForward,
  moveLoading, onMoveBack, isAdmin, onDeleteLineItem, expandedBatches, toggleBatchHistory,
}) {
  const navigate = useNavigate();
  const childItems = item.is_split ? order.line_items.filter(li => li.parent_line_item_id === item.id) : [];
  const placed = item.created_at || order.created_at || order.order_date;
  const unclassified = item.order_type === UNCLASSIFIED;
  const activeBatch = item.batches?.find(b => b.status === 'in_progress');
  const currentStage = activeBatch && item.production_stages?.[activeBatch.current_stage_index || 0];
  const canSend = canSendExternal && item.status !== 'completed' && currentStage?.supports_external_vendor === true;
  const completion = item.job_card_completion;

  return (
    <Card className="mt-3" testID={`item-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {item.item_name || item.name || 'Item'}
          </p>
          <Caption className="mt-0.5">
            <span className="font-medium uppercase" style={{ color: unclassified ? Colors.semantic.caution : Colors.text.tertiary }}>
              {getProductTypeName(item.order_type, true)}
            </span>
            {!item.item_name && item.product_subtype && <> · {item.product_subtype}</>}
            {' · '}Qty {item.quantity?.toLocaleString()}
            {item.surplus_quantity > 0 && ` + ${item.surplus_quantity} surplus`}
            {order.size_unit ? ` ${order.size_unit}` : ''}
          </Caption>
          <Caption className="mt-0.5 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {formatIST(placed, { dateOnly: true })}
            {item.expected_delivery_date && <> → {formatIST(item.expected_delivery_date, { dateOnly: true })}</>}
          </Caption>
        </div>
        {item.status && <StatusPill label={statusLabel(item.status)} tone={statusTone(item.status)} dot />}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {item.delivery_status === 'delivered' && <StatusPill label="Delivered" tone="positive" />}
        {item.is_split && <StatusPill label={`Split into ${childItems.length} types`} tone="caution" />}
        {item.design_status && item.design_status !== 'not_required' && (
          <StatusPill
            label={`Design: ${item.design_status === 'completed' ? 'Done' : item.design_status.replace('_', ' ')}`}
            tone={item.design_status === 'completed' ? 'positive' : 'caution'}
          />
        )}
        {item.fulfill_from_stock && <StatusPill label="From stock" tone="accent" />}
        {item.is_inventory_production && <StatusPill label="Stock production" tone="caution" />}
        {canSeeFinancials && item.item_value > 0 && (
          <StatusPill
            tone="positive"
            label={`₹${item.item_value.toLocaleString()}${
              item.price_type === 'per_piece' && item.price_per_piece > 0 ? ` (₹${item.price_per_piece}/pc)` : ''
            }${item.price_type === 'per_sheet' && item.sheets_used > 0 ? ` (${item.sheets_used} sheets)` : ''}${
              item.item_paid > 0 ? ` · Paid ₹${item.item_paid.toLocaleString()}` : ''
            }`}
          />
        )}
      </div>

      {needsPlate(item) && <PlateBlock item={item} onManage={() => navigate('/plates')} />}

      {item.job_card_id && unclassified && (
        <div
          className="mt-3 rounded-[10px] border px-3 py-2.5"
          style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4' }}
        >
          <p className="flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: Colors.semantic.caution }}>
            <FileText className="h-4 w-4" /> Job card not filled in
          </p>
          <Caption className="mt-0.5">Pick the order type and the processes it runs to start production.</Caption>
        </div>
      )}

      {item.job_card_id && !unclassified && (
        <div className="mt-3 flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0" style={{ color: Colors.accent.DEFAULT }} />
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold" style={{ color: Colors.text.primary }}>Job card</p>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: Colors.background.tertiary }}>
              <div
                className="h-full rounded-full"
                style={{ width: `${completion?.completion_percentage || 0}%`, backgroundColor: Colors.accent.DEFAULT }}
              />
            </div>
          </div>
          <Caption className="shrink-0 tabular-nums">
            {completion?.filled_fields || 0}/{completion?.total_fields || 0} fields
          </Caption>
        </div>
      )}

      <ActionRow
        testID={`item-actions-${idx}`}
        actions={[
          item.job_card_id && {
            key: 'fill', label: unclassified ? 'Fill job card' : 'Fill', icon: Pencil, primary: unclassified,
            testID: `fill-job-card-${idx}`, onClick: () => navigate(`/job-card/${item.job_card_id}/fill`),
          },
          item.job_card_id && !unclassified && {
            key: 'open', label: 'Open job card', icon: ExternalLink, tone: 'accent',
            testID: `open-job-card-${idx}`, onClick: () => navigate(`/job-card/${item.job_card_id}`),
          },
          canSend && {
            key: 'external', label: 'Send to external', icon: ArrowUpDown, tone: 'caution',
            testID: `send-external-btn-${idx}`, onClick: () => openSendExternal(item),
          },
          isAdmin && {
            key: 'delete', label: 'Delete', icon: Trash2, tone: 'critical',
            testID: `delete-item-${idx}`, onClick: () => onDeleteLineItem(item),
          },
        ]}
      />

      {item.production_stages?.length > 0 && !item.is_split && (
        <div className="mt-2 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          <ProductionPipeline stages={item.production_stages} batches={item.batches} />

          {item.batches?.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-medium uppercase tracking-[1px]" style={{ color: Colors.text.tertiary }}>Batches</p>
                <BatchSplitSummary batches={item.batches} />
              </div>
              {item.batches.map(batch => (
                <BatchRow
                  key={batch.id}
                  item={item}
                  batch={batch}
                  canMoveStages={canMoveStages}
                  moveLoading={moveLoading}
                  onMoveBack={onMoveBack}
                  onMoveForward={handleMoveForward}
                  expanded={!!expandedBatches[batch.id]}
                  onToggle={() => toggleBatchHistory(batch.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {item.is_split && childItems.length > 0 && (
        <div className="mt-3 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[1px]" style={{ color: Colors.text.tertiary }}>Types</p>
          {childItems.map(child => (
            <div
              key={child.id}
              className="flex items-center justify-between gap-2 border-b py-2.5 last:border-b-0"
              style={{ borderColor: Colors.border.subtle }}
            >
              <div className="min-w-0">
                <p className="truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>{child.type_name || child.name}</p>
                <Caption>Qty {child.quantity?.toLocaleString()}</Caption>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {child.status && <StatusPill label={statusLabel(child.status)} tone={statusTone(child.status)} />}
                {child.job_card_id && (
                  <button
                    type="button"
                    onClick={() => navigate(`/job-card/${child.job_card_id}`)}
                    className="flex h-9 items-center gap-1 rounded-[10px] border px-3 text-[12px] font-semibold"
                    style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
                  >
                    Job card <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function PhoneLineItems({ order, items, lineItemsSortOrder, toggleLineItemsSort, ...rest }) {
  return (
    <div data-testid="items-phone">
      <div className="flex items-center justify-between gap-2 px-1">
        <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>
          Items <span className="font-medium" style={{ color: Colors.text.tertiary }}>({items.length})</span>
        </h2>
        <button
          type="button"
          onClick={toggleLineItemsSort}
          aria-pressed={lineItemsSortOrder !== 'none'}
          className="flex h-10 items-center gap-1 rounded-[10px] px-2 text-[12px] font-semibold"
          style={{ color: lineItemsSortOrder === 'none' ? Colors.text.secondary : Colors.accent.DEFAULT }}
        >
          <Calendar className="h-3.5 w-3.5" /> Placed
          {lineItemsSortOrder === 'asc' && <ChevronUp className="h-3.5 w-3.5" />}
          {lineItemsSortOrder === 'desc' && <ChevronDown className="h-3.5 w-3.5" />}
          {lineItemsSortOrder === 'none' && <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />}
        </button>
      </div>
      {items.map((item, idx) => <ItemCard key={item.id} item={item} idx={idx} order={order} {...rest} />)}
    </div>
  );
}
